<template>
  <div>
    <div class="d-flex flex-wrap align-center mb-2" style="gap: 12px">
      <v-select v-model="periodMode" :items="periodOptions" label="Период" outlined dense hide-details style="max-width: 200px" />
      <template v-if="periodMode === 'custom'">
        <v-text-field v-model="customFrom" type="date" label="С" outlined dense hide-details style="max-width: 170px" />
        <v-text-field v-model="customTo" type="date" label="По" outlined dense hide-details style="max-width: 170px" />
      </template>
      <v-select
        v-model="owners"
        :items="ownerOptions"
        label="Менеджеры и буфер"
        placeholder="Вся компания"
        multiple small-chips deletable-chips clearable
        outlined dense hide-details
        style="max-width: 340px"
      />
      <v-select
        v-model="testIds"
        :items="testOptions"
        label="Тесты"
        placeholder="Все тесты"
        multiple small-chips deletable-chips clearable
        outlined dense hide-details
        style="max-width: 340px"
      >
        <template v-if="testOptions.length > 1" v-slot:prepend-item>
          <v-list-item ripple @click="toggleAllTests">
            <v-list-item-action>
              <v-icon color="primary">{{ allTestsIcon }}</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>Все</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-divider class="mt-1" />
        </template>
      </v-select>
    </div>
    <div class="caption grey--text text--darken-1 mb-1">{{ periodHint }}</div>
    <div class="caption grey--text text--darken-1 mb-4">
      Буфер и менеджеров вместе выбрать нельзя: раздача из буфера менеджеру попала бы в обе суммы дважды. Менеджеров между собой выбирать можно.
    </div>

    <v-alert v-if="rangeError" type="warning" dense outlined>{{ rangeError }}</v-alert>
    <StateBlock v-else-if="loading && !summary" state="loading" />
    <StateBlock v-else-if="error && error.status === 403" state="forbidden" />
    <StateBlock v-else-if="error" state="error" @retry="load" />

    <template v-else-if="summary">
      <div class="analytics-tiles mb-4">
        <div v-for="tile in tiles" :key="tile.key" class="analytics-tiles__tile">
          <div class="analytics-tiles__label">
            {{ tile.label }}
            <v-tooltip bottom max-width="320">
              <template v-slot:activator="{ on, attrs }">
                <v-icon small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
              </template>
              <span>{{ tile.hint }}</span>
            </v-tooltip>
          </div>
          <div class="analytics-tiles__value" :class="{ 'error--text': tile.value < 0 }">{{ tile.value }} <small class="grey--text" style="font-size: 13px; font-weight: 400">шт.</small></div>
        </div>
      </div>

      <div class="analytics-block mb-4">
        <div class="d-flex align-center mb-2">
          <h3 class="font-md">Получено и израсходовано</h3>
          <v-tooltip bottom max-width="320">
            <template v-slot:activator="{ on, attrs }">
              <v-icon small class="ml-1" color="grey" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
            </template>
            <span>Движения тестов по интервалам. Остаток на прошлые даты сервер не хранит, поэтому графика остатка нет.</span>
          </v-tooltip>
          <v-spacer />
          <v-btn-toggle v-model="groupBy" mandatory dense color="primary">
            <v-btn small value="day">По дням</v-btn>
            <v-btn small value="month">По месяцам</v-btn>
          </v-btn-toggle>
        </div>
        <StateBlock v-if="dynamicsLoading" state="loading" compact />
        <StateBlock v-else-if="dynamicsError" state="error" compact text="Не удалось загрузить график." @retry="loadDynamics" />
        <StateBlock v-else-if="!hasMovements" state="empty" compact icon="mdi-chart-bar" title="Операций за период нет" text="Остаток не изменился." />
        <div v-else style="position: relative; height: 280px">
          <WalletFlowChart
            style="position: relative; height: 100%"
            :points="chartPoints"
            :received-label="labels.received"
            :spent-label="labels.spent"
          />
        </div>
      </div>

      <v-data-table
        :headers="tableHeaders"
        :items="tableRows"
        item-key="id"
        disable-pagination
        hide-default-footer
        disable-sort
        no-data-text="Тестов нет"
      />
    </template>
  </div>
</template>

<script>
import moment from 'moment';
import * as XLSX from 'xlsx';
import api from '@/common/api';
import StateBlock from '@/components/ui/StateBlock.vue';
import { findWalletStatistics, parseWalletDynamics, parseWalletStatistics } from '@/common/models/walletStatistics';
import WalletFlowChart from './WalletFlowChart.vue';
import { PERIOD_HINT } from './walletPeriods';

const FORMAT = 'YYYY-MM-DD';
// Сервер принимает период до 3660 дней
const MAX_DAYS = 3660;
// Локаль moment в проекте не подключена — месяцы подписываем сами
const MONTHS = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

// Аналитика корпа (Э-7). Сводку и график запрашиваем с одинаковыми датами — тогда обороты совпадают.
export default {
  name: 'ClientAnalytics',
  components: { StateBlock, WalletFlowChart },
  props: {
    corpId: { type: [Number, String], required: true },
    company: { type: String, default: '' },
    // Корп смотрит себя: сводка приходит в get-me
    self: { type: Boolean, default: false },
  },
  data: () => ({
    periodMode: 'month',
    periodOptions: [
      { value: 'month', text: 'Текущий месяц' },
      { value: 'prev_month', text: 'Прошлый месяц' },
      { value: 'quarter', text: 'Текущий квартал' },
      { value: '30d', text: 'Последние 30 дней' },
      { value: 'custom', text: 'Свой диапазон' },
    ],
    customFrom: moment().subtract(29, 'days').format(FORMAT),
    customTo: moment().format(FORMAT),
    // Пусто — вся компания; иначе значения 'buffer' и 'm:<id менеджера>' (суммируются)
    owners: [],
    // Пусто — все тесты
    testIds: [],
    groupBy: 'day',
    summary: null,
    dynamics: null,
    loading: false,
    error: null,
    dynamicsLoading: false,
    dynamicsError: false,
    periodHint: PERIOD_HINT,
    requestId: 0,
    dynamicsKey: '',
  }),
  computed: {
    range() {
      const today = moment();
      switch (this.periodMode) {
        case 'prev_month': {
          const prev = today.clone().subtract(1, 'month');
          return { from: prev.clone().startOf('month').format(FORMAT), to: prev.clone().endOf('month').format(FORMAT) };
        }
        case 'quarter': return { from: today.clone().startOf('quarter').format(FORMAT), to: today.format(FORMAT) };
        case '30d': return { from: today.clone().subtract(29, 'days').format(FORMAT), to: today.format(FORMAT) };
        case 'custom': return { from: this.customFrom, to: this.customTo };
        default: return { from: today.clone().startOf('month').format(FORMAT), to: today.format(FORMAT) };
      }
    },
    rangeError() {
      const { from, to } = this.range;
      if (!from || !to) return 'Укажите обе даты периода.';
      if (moment(from).isAfter(moment(to))) return 'Дата начала позже даты окончания.';
      if (moment(to).diff(moment(from), 'days') > MAX_DAYS) return 'Период слишком длинный — не больше 10 лет.';
      return '';
    },
    managers() {
      return this.summary ? this.summary.managers : [];
    },
    ownerOptions() {
      return [
        { value: 'buffer', text: 'Буфер компании' },
        ...this.managers.map((m) => ({ value: `m:${m.id}`, text: `Менеджер: ${m.name}` })),
      ];
    },
    onlyBuffer() {
      return this.owners.length === 1 && this.owners[0] === 'buffer';
    },
    onlyManagers() {
      return this.owners.length > 0 && !this.owners.includes('buffer');
    },
    testOptions() {
      const items = this.summary ? this.summary.items : [];
      return items.map((i) => ({ value: i.id, text: i.name }));
    },
    // Пустой выбор тоже значит «все тесты»: так считают таблица, график и выгрузка
    allTestsSelected() {
      return !this.testIds.length || this.testIds.length === this.testOptions.length;
    },
    allTestsIcon() {
      if (this.allTestsSelected) return 'mdi-checkbox-marked';
      return 'mdi-minus-box';
    },
    items() {
      if (!this.summary) return [];
      return this.summary.items.filter((i) => this.testSelected(i.id));
    },
    labels() {
      if (this.onlyBuffer) return { received: 'Получено', spent: 'Ушло из буфера' };
      if (this.onlyManagers) return { received: 'Получено от компании', spent: 'Израсходовано' };
      return { received: 'Получено', spent: 'Израсходовано' };
    },
    // Строка выбранных владельцев для теста: вся компания, либо сумма по выбранным буферу и менеджерам.
    sliceRow() {
      return (item) => {
        if (!this.owners.length) return item.total;
        return this.pickOwners(item.buffer, item.managers);
      };
    },
    totals() {
      return this.items.reduce((acc, item) => {
        const row = this.sliceRow(item);
        return { balance: acc.balance + row.balance, received: acc.received + row.received, spent: acc.spent + row.spent };
      }, { balance: 0, received: 0, spent: 0 });
    },
    tiles() {
      const spentHint = this.onlyBuffer
        ? 'Сколько ушло из буфера за период: раздано менеджерам и назначено кандидатам.'
        : 'Сколько тестов назначено кандидатам за период. Это не «пройдено».';
      return [
        {
 key: 'balance', label: 'Остаток сейчас', value: this.totals.balance, hint: 'Остаток на текущий момент, от периода не зависит.'
},
        {
          key: 'received',
          label: `${this.labels.received} за период`,
          value: this.totals.received,
          hint: this.onlyManagers ? 'Сколько тестов компания распределила менеджерам за период.' : 'Поступления за период: начисления и оплаченные заказы.',
        },
        {
 key: 'spent', label: `${this.labels.spent} за период`, value: this.totals.spent, hint: spentHint
},
      ];
    },
    tableHeaders() {
      return [
        { text: 'Тест', value: 'name' },
        { text: 'Остаток сейчас', value: 'balance', align: 'end' },
        { text: `${this.labels.received}*`, value: 'received', align: 'end' },
        { text: `${this.labels.spent}*`, value: 'spent', align: 'end' },
      ];
    },
    tableRows() {
      return this.items
        // id и name — после разреза: у строки менеджера есть свои id и name, они не должны затирать тест.
        .map((item) => ({ ...this.sliceRow(item), id: item.id, name: item.name }))
        .filter((r) => r.balance || r.received || r.spent);
    },
    // Точки графика выбранного разреза; интервалы у всех тестов одинаковые — складываем по индексу.
    chartPoints() {
      if (!this.dynamics) return [];
      const series = this.dynamics.items.filter((i) => this.testSelected(i.id));
      if (!series.length) return [];
      const base = series[0].points;
      return base.map((p, ind) => {
        let received = 0;
        let spent = 0;
        series.forEach((s) => {
          const point = s.points[ind];
          if (!point) return;
          const row = this.owners.length ? this.pickOwners(point.buffer, point.managers) : point.total;
          received += row.received;
          spent += row.spent;
        });
        return {
          label: this.pointLabel(p), from: this.formatDay(p.from), to: this.formatDay(p.to), received, spent,
        };
      });
    },
    hasMovements() {
      return this.chartPoints.some((p) => p.received || p.spent);
    },
  },
  watch: {
    range() {
      this.load();
    },
    // Тот же экран открыли для другого корпа: выбор владельцев и тестов прежней компании не переносим
    corpId() {
      this.owners = [];
      this.testIds = [];
      this.summary = null;
      this.dynamics = null;
      this.dynamicsKey = '';
      this.load();
    },
    groupBy() {
      this.loadDynamics();
    },
    // Буфер и менеджеры не комбинируются: остаётся то, что выбрано последним
    owners(now, before) {
      if (!now.includes('buffer') || now.length < 2) return;
      this.owners = before.includes('buffer') ? now.filter((o) => o !== 'buffer') : ['buffer'];
    },
    testIds(now, before) {
      const single = (ids) => (ids.length === 1 ? String(ids[0]) : null);
      if (single(now) !== single(before)) this.loadDynamics();
    },
    periodMode(val) {
      // Квартал — три месячных столбца
      if (val === 'quarter') this.groupBy = 'month';
    },
  },
  methods: {
    testSelected(id) {
      return !this.testIds.length || this.testIds.some((t) => String(t) === String(id));
    },
    // Сумма по выбранным владельцам: буфер и/или менеджеры (строки одного теста).
    pickOwners(buffer, managers) {
      const rows = [];
      if (this.owners.includes('buffer')) rows.push(buffer);
      this.owners.filter((o) => o.startsWith('m:')).forEach((o) => {
        const row = managers.find((m) => String(m.id) === o.slice(2));
        if (row) rows.push(row);
      });
      return rows.reduce((acc, r) => ({
        balance: acc.balance + (r.balance || 0), received: acc.received + r.received, spent: acc.spent + r.spent,
      }), { balance: 0, received: 0, spent: 0 });
    },
    formatDay(value) {
      return value ? moment(value).format('DD.MM.YYYY') : '';
    },
    pointLabel(p) {
      if (this.groupBy === 'day') return moment(p.from).format('DD.MM');
      const from = moment(p.from);
      const to = moment(p.to);
      const fullMonth = from.date() === 1 && to.isSame(to.clone().endOf('month'), 'day');
      // Неполный месяц подписываем датами, чтобы не выглядел полным
      return fullMonth ? `${MONTHS[from.month()]} ${from.year()}` : `${from.format('DD.MM')}–${to.format('DD.MM')}`;
    },
    async load() {
      if (this.rangeError) return;
      this.requestId += 1;
      const { requestId } = this;
      const { from, to } = this.range;
      this.loading = true;
      this.error = null;
      try {
        const params = { from_date: from, to_date: to };
        let raw = null;
        if (this.self) raw = findWalletStatistics(await api.get('/users/get-me', { params }));
        if (!raw) raw = (await api.get(`/users/corp_clients/${this.corpId}/`, { params })).wallet_statistics;
        if (requestId !== this.requestId) return;
        this.summary = parseWalletStatistics(raw);
        // Менеджера, которого больше нет в сводке, убираем из выбора
        const kept = this.owners.filter((o) => !o.startsWith('m:') || this.managers.some((m) => `m:${m.id}` === o));
        if (kept.length !== this.owners.length) this.owners = kept;
      } catch (err) {
        if (requestId !== this.requestId) return;
        this.summary = null;
        this.error = { status: (err && err.status) || null };
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
      this.loadDynamics();
    },
    async loadDynamics() {
      if (this.rangeError) return;
      const { from, to } = this.range;
      // Сервер фильтрует динамику по одному тесту; для нескольких берём все и отбираем на экране
      const single = this.testIds.length === 1 ? this.testIds[0] : null;
      const key = `${from}|${to}|${this.groupBy}|${single}`;
      this.dynamicsKey = key;
      this.dynamicsLoading = true;
      this.dynamicsError = false;
      try {
        const params = { from_date: from, to_date: to, group_by: this.groupBy };
        if (single != null) params.nomenclature_id = single;
        const raw = await api.get(`/users/corp_clients/${this.corpId}/wallet-dynamics/`, { params });
        if (this.dynamicsKey !== key) return;
        this.dynamics = parseWalletDynamics(raw);
      } catch (e) {
        if (this.dynamicsKey !== key) return;
        this.dynamics = null;
        this.dynamicsError = true;
      } finally {
        if (this.dynamicsKey === key) this.dynamicsLoading = false;
      }
    },
    // «Все»: сбрасывает выбор отдельных тестов — считаются все
    toggleAllTests() {
      this.testIds = [];
    },
    exportXlsx() {
      if (!this.summary) return;
      const { from, to } = this.range;
      const names = (options, values) => options.filter((o) => values.some((v) => String(v) === String(o.value))).map((o) => o.text);
      const slice = this.owners.length ? names(this.ownerOptions, this.owners).join(', ') : 'Вся компания';
      const test = this.testIds.length ? names(this.testOptions, this.testIds).join(', ') : 'Все тесты';
      const head = [
        [this.company || 'Корп. клиент'],
        [`Период: ${this.formatDay(from)} — ${this.formatDay(to)}`],
        [`Разрез: ${slice}; тесты: ${test}`],
        [],
      ];
      const table = [['Тест', 'Остаток сейчас', this.labels.received, this.labels.spent],
        ...this.tableRows.map((r) => [r.name, r.balance, r.received, r.spent])];
      const chart = [['Интервал', 'С', 'По', this.labels.received, this.labels.spent],
        ...this.chartPoints.map((p) => [p.label, p.from, p.to, p.received, p.spent])];
      const wb = XLSX.utils.book_new();
      const wsTable = XLSX.utils.aoa_to_sheet([...head, ...table]);
      wsTable['!cols'] = [{ wch: 32 }, { wch: 16 }, { wch: 22 }, { wch: 22 }];
      XLSX.utils.book_append_sheet(wb, wsTable, 'По тестам');
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([...head, ...chart]), 'Динамика');
      XLSX.writeFile(wb, `Аналитика${this.company ? ` — ${this.company}` : ''}.xlsx`.replace(/[\\/:*?"<>|]/g, ''));
    },
  },
  created() {
    this.load();
  },
};
</script>

<style scoped lang="scss">
.analytics-tiles {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;

  &__tile {
    padding: 16px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: #fff;
  }

  &__label {
    display: flex;
    align-items: center;
    font-size: 13px;
    color: #757575;
  }

  &__value {
    margin-top: 6px;
    font-size: 26px;
    font-weight: 600;
  }
}

.analytics-block {
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
}
</style>
